import { Grid, Typography } from '@material-ui/core';
import { CheckCircle } from '@material-ui/icons';
import { investidorPfDocumentTypeDescriptions, investidorPjDocumentTypeDescriptions } from 'common/genericDescriptions';
import { investidorPfDocumentTypeOptions, investidorPjDocumentTypeOptions } from 'common/genericOptions';
import { InvestidorDocumentType } from 'common/genericTypes';
import UploadDocs, { AcceptableFileTypes, Attachment } from 'components/uploadDocs';
import { Dropdown, Section } from 'm2m.components';
import React, { useState } from 'react';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'store';
import { updateDocumentos, updateDocumentosExcluir } from 'store/modules/investidores/registro';
import { addAlert } from 'store/modules/snackBar';

const mapStateToProps = (state: RootState) => ({
  investidor: state.investidor,
  isPj: state.investidor.informacoesPrincipais.isPj,
  documentos: state.investidor.documentos,
  documentosExcluir: state.investidor.documentosExcluir,
});

const mapDispatchToProps = { updateDocumentos, updateDocumentosExcluir, addAlert };

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const Documentos: React.FC<Props> = (props) => {
  const { investidor, isPj, documentos, documentosExcluir, updateDocumentos, updateDocumentosExcluir, addAlert } =
    props;
  const investidorDocumentTypeOptions = isPj ? investidorPjDocumentTypeOptions : investidorPfDocumentTypeOptions;
  const investidorDocumentTypeDescriptions = isPj
    ? investidorPjDocumentTypeDescriptions
    : investidorPfDocumentTypeDescriptions;

  const [tipoDocumento, setTipoDocumento] = useState<string>('');

  const handleUpdateFile = (attachments: Attachment[]) => {
    const newAttachment = attachments?.[0];
    const newDocumentsInfo = documentos.map((documentInfo) => {
      if (documentInfo.type === newAttachment.id) {
        return { ...documentInfo, file: newAttachment.file };
      }
      return documentInfo;
    });
    updateDocumentos(newDocumentsInfo);
  };

  const handleDeleteFile = (type: string) => {
    for (const document of documentos) {
      if (document.type === type && !!document.remoteFileInfo) {
        updateDocumentosExcluir((documentosExcluir || [])?.concat({ ...document }));
      }
    }

    const newDocumentsInfo = documentos.map((documentInfo) => {
      if (documentInfo.type === type) {
        const { file, remoteFileInfo, ...rest } = documentInfo;
        return rest;
      }
      return documentInfo;
    });
    updateDocumentos(newDocumentsInfo);
  };

  useEffect(() => {
    const docs = investidorDocumentTypeOptions.map((doc) => {
      let documentUploaded = documentos.find((docInvestidor) => docInvestidor.type === doc.value);
      const hasRepresentanteLegal = investidor.representantesLegais.some((_) => _.cpf);
      switch (doc.value) {
        case InvestidorDocumentType.ContratoSocial:
          return {
            type: doc.value,
            file: undefined,
            optional: !isPj,
            ...documentUploaded,
          };
        case InvestidorDocumentType.IdentidadeRepresentanteLegalProcurador:
          return {
            type: doc.value,
            file: undefined,
            optional: !hasRepresentanteLegal,
            ...documentUploaded,
          };
        case InvestidorDocumentType.Procuracao:
          return {
            type: doc.value,
            file: undefined,
            optional: !(!isPj && hasRepresentanteLegal),
            ...documentUploaded,
          };
        default:
          return { type: doc.value, file: undefined, optional: false, ...documentUploaded };
      }
    });

    updateDocumentos(docs);
  }, [investidor.informacoesPrincipais.cpfCnpj, investidor.representantesLegais, updateDocumentos]);

  return (
    <Section title={'Upload de Documentos'}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Dropdown
            label={'Tipo de Documento'}
            required
            options={investidorDocumentTypeOptions}
            value={tipoDocumento}
            onChange={(value) => setTipoDocumento(value)}
          />
        </Grid>
        <Grid item xs={12}>
          <UploadDocs
            inputAttachments={documentos
              ?.filter((attachment) => !!attachment.remoteFileInfo)
              .map(({ type, remoteFileInfo }) => ({
                id: type,
                remoteFileInfo,
              }))}
            fileId={tipoDocumento}
            disabled={!tipoDocumento}
            onChange={handleUpdateFile}
            onDelete={handleDeleteFile}
            overrideAcceptableFileTypes={[
              AcceptableFileTypes.AppPdf,
              AcceptableFileTypes.ImageJpeg,
              AcceptableFileTypes.Zip,
              AcceptableFileTypes.XZipCompressed,
            ]}
            onError={(error) => addAlert({ severity: 'error', message: error })}
            dataTestId="btnSelecionarDocumentoInvestidor"
          />
        </Grid>
        <Grid item xs={12}>
          {documentos.map((document) =>
            !document.optional || !!document.file || !!document.remoteFileInfo ? (
              <React.Fragment key={document.type}>
                <Grid container spacing={1}>
                  <Grid item>
                    <CheckCircle color={document.file || document.remoteFileInfo ? 'primary' : 'disabled'} />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body2">{investidorDocumentTypeDescriptions[document.type]}</Typography>
                  </Grid>
                </Grid>
              </React.Fragment>
            ) : null
          )}
        </Grid>
      </Grid>
    </Section>
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(Documentos);
